import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, AlertCircle, X, Clipboard, Square } from 'lucide-react';
import { UseLegalAIReturn } from '../../../../hooks/useLegalAI';
import { QuickAction } from '../../../../services/legalAIService';
import { LLMModeSelector } from './LLMModeSelector';
import { QuickActions } from './QuickActions';
import { ConversationDisplay } from './ConversationDisplay';
import { ConversationSearch } from './ConversationSearch';

interface AIWorkspaceAreaProps {
  legalAI: UseLegalAIReturn;
  activeClause: string | null;
  onClauseSelect: (clauseText: string) => void;
  hasContract: boolean;
  contractId?: string;
}

export const AIWorkspaceArea: React.FC<AIWorkspaceAreaProps> = ({
  legalAI,
  activeClause,
  onClauseSelect,
  hasContract,
  contractId
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const [clauseInput, setClauseInput] = useState('');
  const [showClauseInput, setShowClauseInput] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const clauseInputRef = useRef<HTMLTextAreaElement>(null);

  // Update clause input when active clause changes
  useEffect(() => {
    if (activeClause) {
      setClauseInput(activeClause);
      setShowClauseInput(true);
    }
  }, [activeClause]);

  // Focus input after loading completes
  useEffect(() => {
    if (!legalAI.isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [legalAI.isLoading]);

  const handleSend = async () => {
    if (!inputMessage.trim() || legalAI.isLoading || legalAI.isStreaming) return;

    try {
      await legalAI.sendMessage(inputMessage, clauseInput || undefined);
      setInputMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleCancelStream = () => {
    if (legalAI.cancelStream) {
      legalAI.cancelStream();
    }
  };

  const handleQuickAction = async (action: QuickAction) => {
    if (!clauseInput.trim()) {
      setShowClauseInput(true);
      clauseInputRef.current?.focus();
      return;
    }

    try {
      await legalAI.executeQuickActionAction(action, clauseInput);
    } catch (err) {
      console.error('Failed to execute action:', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setClauseInput(text);
      setShowClauseInput(true);
    } catch (err) {
      console.error('Failed to paste:', err);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-dark-bg">
      {/* Mode Selector and Search */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border px-4 py-3 space-y-3">
        <ConversationSearch
          onSearch={legalAI.searchConversations}
          onClearSearch={legalAI.clearSearch}
          onSelectConversation={legalAI.selectConversation}
          searchResults={legalAI.searchResults}
          isSearching={legalAI.isSearching}
          searchQuery={legalAI.searchQuery}
          contractId={contractId}
        />
        <LLMModeSelector
          mode={legalAI.mode}
          onChange={legalAI.setMode}
        />
      </div>

      {/* Conversation Area */}
      <div className="flex-1 overflow-y-auto p-4">
        {!hasContract && legalAI.conversationHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">Select a Contract</p>
            <p className="text-sm mt-1 text-center max-w-md">
              Choose a contract from the list on the left to start analyzing and getting AI-powered legal insights.
            </p>
          </div>
        ) : (
          <ConversationDisplay
            messages={legalAI.conversationHistory}
            isLoading={legalAI.isLoading}
            isStreaming={legalAI.isStreaming}
            streamingContent={legalAI.streamingContent}
          />
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-dark-surface border-t border-gray-200 dark:border-dark-border px-4 py-2">
        <QuickActions onAction={handleQuickAction} disabled={legalAI.isLoading} />
      </div>

      {/* Clause Input (Collapsible) */}
      <div className="bg-white dark:bg-dark-surface border-t border-gray-200 dark:border-dark-border">
        <button
          onClick={() => setShowClauseInput(!showClauseInput)}
          className="w-full px-4 py-2 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Clipboard className="w-4 h-4" />
            {clauseInput ? 'Clause text attached' : 'Attach clause text (optional)'}
          </span>
          <span className="text-xs">
            {showClauseInput ? 'Hide' : 'Show'}
          </span>
        </button>

        {showClauseInput && (
          <div className="px-4 pb-3">
            <div className="relative">
              <textarea
                ref={clauseInputRef}
                value={clauseInput}
                onChange={(e) => setClauseInput(e.target.value)}
                placeholder="Paste a clause here for analysis..."
                className="w-full px-3 py-2 pr-20 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
              />
              <div className="absolute top-2 right-2 flex gap-1">
                <button
                  onClick={handlePaste}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                  title="Paste from clipboard"
                >
                  <Clipboard className="w-4 h-4" />
                </button>
                {clauseInput && (
                  <button
                    onClick={() => setClauseInput('')}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                    title="Clear clause"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {clauseInput && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {clauseInput.length} characters
              </p>
            )}
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="bg-white dark:bg-dark-surface border-t border-gray-200 dark:border-dark-border px-4 py-3">
        {legalAI.error && (
          <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2 text-red-700 dark:text-red-300 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p>{legalAI.error}</p>
              <button
                onClick={legalAI.clearError}
                className="text-xs underline mt-1 hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={hasContract ? "Ask about the contract or clause..." : "Select a contract to start..."}
            className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg resize-none text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={2}
            disabled={legalAI.isLoading || legalAI.isStreaming}
          />
          {legalAI.isStreaming ? (
            <button
              onClick={handleCancelStream}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center min-w-[48px] transition-colors"
              title="Stop generation"
            >
              <Square className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={legalAI.isLoading || !inputMessage.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[48px] transition-colors"
            >
              {legalAI.isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

export default AIWorkspaceArea;
